import LoadingCircle from "reports/components/simple/LoadingCircle";
import EOSReport from "../../../../components/Reports/EOSReport";
import SbdwarringtonSummaryMetrics from "./SbdwarringtonSummaryMetrics";
import { isEmpty } from "lodash";
import MetricAndPnLBreakdowns from "../../../../components/Reports/GenericCharts/MetricAndPnLBreakdowns/MetricAndPnLBreakdowns";
import {
  useGeAndLtFromUrl,
  useProcessGroupNameAndMachemetryIds,
} from "../../../../components/Reports/reportUtils";
import PnLChart from "../../../../components/Reports/GenericCharts/PnLChart/PnLChart";
import { getPnlTimesWithProducingLosses } from "../../../../components/Reports/GenericCharts/PnLChart/pnlChartUtils";
import { ONE_DAY_OF_MS, ONE_HOUR_OF_MS } from "reports/utils/timeUtils";
import useEosData from "reports/store/useEosData";

import { ProducingLossesEnum } from "reports/types";
import { MetricsTrendChart } from "reports/components/Reports/GenericCharts/MetricsTrendChart";
import { lastNWeekDataAtom } from "reports/store/store";
import { useAtom } from "jotai";
import { MetricEnum } from "reports/utils/metricTypes";
import {
  getCustomSummaryMetricsInputListWithPnlBreakdown,
  getSummaryMetricsListForShiftAndProcessBreakdowns,
} from "./sbdWarringtonEosUtils";
import { black, producing } from "reports/constants/colors";
import { ShiftPnlBreakdowns } from "reports/components/Reports/GenericCharts/MetricAndPnLBreakdowns/ShiftPnlBreakdowns";

export default function SbdwarringtonEOS() {
  const { processGroupName, machemetryIds } =
    useProcessGroupNameAndMachemetryIds({});

  // EOS needs ge and lt url params, sample is May 5, 2021, 2PM - 10PM
  const sampleLink = `/${processGroupName}?ge=1620190800000&lt=1620277200000`;

  const range = useGeAndLtFromUrl();
  const { ge, lt } = range;

  const reportDuration = range.lt - range.ge;

  const is24Hour = reportDuration / ONE_HOUR_OF_MS === 24;
  const isWeekly = (lt - ge) / ONE_DAY_OF_MS > 3;

  return (
    <>
      {machemetryIds && machemetryIds.length > 0 ? (
        <>
          <EOSReport
            machemetryIds={machemetryIds}
            sampleLink={sampleLink}
            shouldShowPrint={false}
            shouldSkipSkuData={false}
            shouldFetchDataByShift={true}
            shouldFetchLastNWeeksData={isWeekly}
          >
            <SbdwarringtonEosComponents
              {...{
                processGroupName,
                machemetryIds,
                is24Hour,
                isWeekly,
              }}
            />
          </EOSReport>
        </>
      ) : (
        <LoadingCircle
          logAfterTimeout={{
            logData: { machemetryIds },
            delay: 5 * 1000,
          }}
        />
      )}
    </>
  );
}

function SbdwarringtonEosComponents({
  processGroupName,
  machemetryIds,
  isWeekly,
  is24Hour,
}) {
  const { data } = useEosData();
  const [lastNWeekData] = useAtom(lastNWeekDataAtom);

  return (
    <>
      {data && !isEmpty(data) && machemetryIds && machemetryIds.length > 0 ? (
        <>
          <SbdwarringtonSummaryMetrics
            {...{
              data,
              processGroupName,
              is24Hour,
            }}
          />
          <PnLChart
            pnlTimes={getPnlTimesWithProducingLosses({
              data,
              producingLossesToSkip: [ProducingLossesEnum.minorStopLoss],
            })}
          />
          {isWeekly && (
            <MetricsTrendChart
              summaryMetricInputList={[
                {
                  name: MetricEnum.COUNT,
                  title: "Parts Produced",
                  color: black,
                },
                { name: MetricEnum.OEE, color: producing },
              ]}
              pnlBreakdowns={lastNWeekData}
              descendingOrder={false}
            />
          )}
          {(is24Hour || isWeekly) && (
            <ShiftPnlBreakdowns
              summaryMetricsInputList={getSummaryMetricsListForShiftAndProcessBreakdowns()}
              getCustomSummaryMetricsInputListWithPnlBreakdown={pnlBreakdown =>
                getCustomSummaryMetricsInputListWithPnlBreakdown(pnlBreakdown)
              }
              showReportLink={is24Hour}
            />
          )}
          <MetricAndPnLBreakdowns
            machemetryIds={machemetryIds}
            summaryMetricsInputList={getSummaryMetricsListForShiftAndProcessBreakdowns()}
            getCustomSummaryMetricsInputListWithPnlBreakdown={pnlBreakdown =>
              getCustomSummaryMetricsInputListWithPnlBreakdown(pnlBreakdown)
            }
          />
        </>
      ) : (
        <LoadingCircle
          logAfterTimeout={{
            logData: { eosData: data, machemetryIds },
            delay: 5 * 1000,
          }}
        />
      )}
    </>
  );
}
